// @flow
import BaseCollection from "../base/BaseCollection";
import { IllegalArgumentException } from "../../exceptions";

/**
 * Collection of layouthint
 */
export default class LayoutHintCollection extends BaseCollection<string> {
  /**
   * Constructs the layouthint collection
   */
  constructor(layouthint: Array<string> = []) {
    super();

    if (!Array.isArray(layouthint)) {
      throw new IllegalArgumentException(`${layouthint} is not an Array`);
    }

    this.collection = layouthint;
  }

  /**
   * Retrieve layout hints
   */
  get layouthint(): Array<string> {
    return this.collection;
  }

  /**
   * Checks if a specific layouthint exists in the component configuration
   * This method matches all exact matches and hints starting with. For example
   * when calling layouth.has("hide"), this will return true for both "hide" and "hide-when-empty".<br>
   * If only exact matches are required, use the method {@link LayoutHintCollection#hasExact}.
   */
  has(...hints: Array<string>): boolean {
    const hintArray = hints.length > 0 ? [...hints] : [];

    return hintArray
      .flat()
      .some(
        (hint) =>
          this.layouthint.includes(hint) ||
          this.layouthint.some((hint2) => hint2.startsWith(hint)),
      );
  }

  /**
   * Checks if a specific layouthint exists in the component configuration,
   * only checks on exact matches.<br>
   * If layouthints that start with the given hint should also match, use {@link LayoutHintCollection#has}.
   * @param hints
   * @returns {boolean}
   */
  hasExact(...hints: Array<string>): boolean {
    const hintArray = hints.length > 0 ? [...hints] : [];
    return hintArray.flat().some((hint) => this.layouthint.includes(hint));
  }

  /**
   * Retrieve the first layouthint starting with hint
   */
  getByLayoutHint(hint: Function | string): null | string {
    let foundHint = null;

    if (typeof hint === "function") {
      foundHint = this.layouthint.find(hint);
    }

    if (typeof hint === "string") {
      foundHint = this.layouthint.find((hint2) =>
        hint2.startsWith(hint.toString()),
      );
    }

    return foundHint || null;
  }

  /**
   * Get the value of a specific hint
   * Format: hint=value
   */
  getLayoutHintValue(hint: string): ?string {
    const checkHint = hint.endsWith(":")
      ? hint.substr(0, hint.length - 1)
      : hint;

    const match = this.getByLayoutHint(
      (hint2) =>
        hint2.startsWith(`${checkHint}=`) || hint2.startsWith(`${checkHint}:`),
    );

    if (!match) {
      return null;
    }

    return match.substr(checkHint.length + 1);
  }

  /**
   * Get an array of multiple values from a specific hint
   * Format: hint=value1;value2;value3
   */
  getLayoutHintValues(hint: string): Array<string> | null {
    if (!this.has(hint)) {
      return null;
    }

    const value = this.getLayoutHintValue(hint);

    if (!value) {
      return [];
    }

    return value.split(";");
  }
}
